import { useState, useEffect, lazy, Suspense } from "react";
import { WEB_URL } from "@/lib/webUrl";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type {
  Trip,
  TripDate,
  Destination,
  Guide,
  PremiumUpgrade,
  TripPremiumUpgrade,
} from "@ckgo2/shared/types";
import type { UseFormReturn } from "react-hook-form";
import { normalizeMeals } from "@ckgo2/shared/utils";
import {
  ArrowLeft,
  Save,
  Loader2,
  FileText,
  Map,
  Image,
  Calendar,
  Clock,
  ExternalLink,
  LayoutList,
  Boxes,
  Calculator,
} from "lucide-react";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";
import ErrorBanner from "@/components/common/ErrorBanner";
import { useEditPage } from "@/hooks/useEditPage";
import { TRIP_FIELD_TO_TAB, getFirstTabWithErrors, getTabsWithErrors } from "@/lib/fieldTabMap";
import type { TripFormData, TripComponentRow, CatalogComponent } from "@/components/trips";

const BasicInfoTab = lazy(() => import("@/components/trips/BasicInfoTab"));
const ItineraryTab = lazy(() => import("@/components/trips/ItineraryTab"));
const IncludedTab = lazy(() => import("@/components/trips/IncludedTab"));
const GalleryTab = lazy(() => import("@/components/trips/GalleryTab"));
const ContentTab = lazy(() => import("@/components/trips/ContentTab"));
const DatesTab = lazy(() => import("@/components/trips/DatesTab"));
const ComponentsTab = lazy(() => import("@/components/trips/ComponentsTab"));
const ExperienceTab = lazy(() => import("@/components/trips/ExperienceTab"));
const CalculationTab = lazy(() => import("@/components/trips/CalculationTab"));

const TABS = [
  { id: "basic", label: "Basic info", icon: FileText },
  { id: "itinerary", label: "Itinerary", icon: Map },
  { id: "experience", label: "Experience details", icon: Clock },
  { id: "included", label: "What's included", icon: FileText },
  { id: "gallery", label: "Gallery", icon: Image },
  { id: "dates", label: "Dates", icon: Calendar },
  { id: "components", label: "Components", icon: Boxes },
  { id: "calculation", label: "Calculation", icon: Calculator },
  { id: "content", label: "Content", icon: LayoutList },
];

function transformTripTextFields(t: Trip) {
  return {
    title: t.title || "",
    slug: t.slug || "",
    destinationId: t.destinationId || "",
    guideId: t.guideId || "",
    guideVideoUrl: t.guideVideoUrl || "",
    shortDescription: t.shortDescription || "",
    description: t.description || "",
    featuredImage: t.featuredImage || "",
    featuredImageId: t.featuredImageId ?? null,
    heroTitle: t.heroTitle || "",
    heading: t.heading || "",
    metaTitle: t.metaTitle || "",
    metaDescription: t.metaDescription || "",
    mapImage: t.mapImage || "",
    mapImageId: t.mapImageId ?? null,
    note: t.note || "",
    guideRecommendation: t.guideRecommendation || "",
    defaultMeetingPoint: t.defaultMeetingPoint || "",
    defaultMeetingTime: t.defaultMeetingTime || "",
  };
}

function transformTripNumericAndFlagFields(t: Trip) {
  // Use ?? for nullable numbers/booleans to preserve 0/false; || for strings where empty is falsy.
  // durationHours stays null for tours (schema rejects 0 via .min(1)).
  return {
    durationDays: t.durationDays ?? 7,
    durationHours: t.durationHours ?? null,
    priceFrom: t.priceFrom ?? 0,
    priceOriginal: t.priceOriginal ?? null,
    difficultyLevel: t.difficultyLevel || "medium",
    tripType: t.tripType || "guided",
    tripKind: t.tripKind || "tour",
    fulfillmentMode: t.fulfillmentMode || "fixed_departures",
    countryCodesText: (t.countryCodes || ["VN"]).join(", "),
    optionalActivities: t.optionalActivities || [],
    tripStyle: t.tripStyle || "",
    groupSizeMin: t.groupSizeMin ?? 8,
    groupSizeMax: t.groupSizeMax ?? 16,
    leadTimeHours: t.leadTimeHours ?? 24,
    status: t.status || "draft",
    isFeatured: t.isFeatured ?? false,
    isLastMinute: t.isLastMinute ?? false,
    hasCzechGuide: t.hasCzechGuide ?? false,
    allowsOnlineBooking: t.allowsOnlineBooking ?? true,
  };
}

function transformTripArrayFields(t: Trip) {
  return {
    itinerary: (t.itinerary || []).map((day) => ({
      day: day.day,
      title: day.title,
      description: day.description || "",
      imageId: day.imageId ?? null,
      meals: normalizeMeals(day.meals),
      accommodation: day.accommodation || "",
      highlights: day.highlights || [],
      guideTip: day.guideTip || "",
    })),
    galleryImages: (t.galleryImages || []).map((url) => ({
      url,
      alt: "",
      isPrimary: false,
    })),
    galleryImageIds: t.galleryImageIds || [],
    included: (t.included || []).map((text) => ({ text })),
    excluded: (t.excluded || []).map((text) => ({ text })),
    tags: t.tags || [],
    dates: ((t as Trip & { dates?: TripDate[] }).dates || []).map((date: TripDate) => ({
      id: date.id,
      startDate:
        typeof date.startDate === "string"
          ? date.startDate
          : new Date(date.startDate).toISOString().split("T")[0],
      endDate:
        typeof date.endDate === "string"
          ? date.endDate
          : new Date(date.endDate).toISOString().split("T")[0],
      price: date.price,
      capacity: date.capacity,
      bookedCount: date.bookedCount || 0,
      lifecycle: date.lifecycle,
    })),
    youtubeVideos: t.youtubeVideos || [],
    faqs:
      Array.isArray(t.faqs) && t.faqs.length > 0 && "question" in t.faqs[0]
        ? [
            {
              topic: "General",
              items: t.faqs as unknown as Array<{ question: string; answer: string }>,
            },
          ]
        : t.faqs || [],
    socialReels: t.socialReels || [],
    upgradeIds: [],
    relatedItems: t.relatedItems || [],
    availableWeekdays: t.availableWeekdays ?? null,
    highlights: t.highlights || [],
    schedule: t.schedule || [],
    components: ((t as Trip & { components?: TripComponentRow[] }).components || []).map((cmp) => ({
      id: cmp.id,
      componentId: cmp.componentId,
      quantity: cmp.quantity ?? 1,
      seedTarget: cmp.seedTarget ?? "order",
      sortOrder: cmp.sortOrder ?? 0,
      notes: cmp.notes ?? null,
      component: cmp.component ?? null,
    })),
  };
}

function transformTripOnLoad(t: Trip): TripFormData {
  return {
    ...transformTripTextFields(t),
    ...transformTripNumericAndFlagFields(t),
    ...transformTripArrayFields(t),
  };
}

function TripTabContent({
  activeTab,
  form,
  destinations,
  guides,
  allUpgrades,
  allTrips,
  componentCatalog,
}: {
  activeTab: string;
  form: UseFormReturn<TripFormData>;
  destinations: Destination[];
  guides: Guide[];
  allUpgrades: PremiumUpgrade[];
  allTrips: Trip[];
  componentCatalog: CatalogComponent[];
}) {
  switch (activeTab) {
    case "basic":
      return <BasicInfoTab form={form} destinations={destinations} guides={guides} />;
    case "itinerary":
      return <ItineraryTab form={form} />;
    case "included":
      return <IncludedTab form={form} />;
    case "gallery":
      return <GalleryTab form={form} />;
    case "content":
      return <ContentTab form={form} allUpgrades={allUpgrades} allTrips={allTrips} />;
    case "dates":
      return <DatesTab form={form} guides={guides} />;
    case "components":
      return <ComponentsTab form={form} catalog={componentCatalog} />;
    case "calculation":
      return <CalculationTab form={form} />;
    case "experience":
      return <ExperienceTab form={form} />;
    default:
      return null;
  }
}

export default function TripEditPage() {
  const [activeTab, setActiveTab] = useState("basic");
  const { id } = useParams<{ id: string }>();
  const isNewTrip = id === "new";

  // Fetch destinations for dropdown
  const { data: destinationsData } = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.get<ApiResponse<Destination[]>>("/api/destinations"),
  });

  // Fetch guides for dropdown
  const { data: guidesData } = useQuery({
    queryKey: ["guides"],
    queryFn: () => api.get<ApiResponse<Guide[]>>("/api/guides"),
  });

  // Fetch all trips for related items picker
  const { data: allTripsData } = useQuery({
    queryKey: ["trips"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips"),
  });

  // Fetch all premium upgrades for selection
  const { data: upgradesData } = useQuery({
    queryKey: ["premiumUpgrades"],
    queryFn: () => api.get<ApiResponse<PremiumUpgrade[]>>("/api/admin/premium-upgrades"),
  });

  // Fetch the priced component catalog (kusovník picker).
  const { data: componentCatalogData } = useQuery({
    queryKey: ["supplierComponentsCatalog"],
    queryFn: () =>
      api.get<ApiResponse<CatalogComponent[]>>("/api/admin/supplier-components?limit=100"),
  });

  // Fetch existing upgrade assignments for this trip
  const { data: upgradeAssignmentsData } = useQuery({
    queryKey: ["tripUpgrades", id],
    queryFn: () => api.get<ApiResponse<TripPremiumUpgrade[]>>(`/api/admin/trips/${id}/upgrades`),
    enabled: !isNewTrip && !!id,
  });

  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<
    TripFormData,
    Trip
  >({
    resourceKey: "trip",
    listKey: "trips",
    apiPath: "/api/admin/trips",
    defaultValues: {
      title: "",
      slug: "",
      destinationId: "",
      guideId: "",
      guideVideoUrl: "",
      durationDays: 7,
      durationHours: null,
      priceFrom: 0,
      priceOriginal: null,
      shortDescription: "",
      description: "",
      featuredImage: "",
      featuredImageId: null,
      difficultyLevel: "medium",
      tripType: "guided",
      tripKind: "tour",
      fulfillmentMode: "fixed_departures",
      countryCodesText: "VN",
      optionalActivities: [],
      tripStyle: "",
      groupSizeMin: 8,
      groupSizeMax: 16,
      leadTimeHours: 24,
      availableWeekdays: null,
      status: "draft",
      isFeatured: false,
      isLastMinute: false,
      hasCzechGuide: false,
      allowsOnlineBooking: true,
      heroTitle: "",
      heading: "",
      metaTitle: "",
      metaDescription: "",
      itinerary: [],
      galleryImages: [],
      galleryImageIds: [],
      included: [],
      excluded: [],
      tags: [],
      dates: [],
      youtubeVideos: [],
      note: "",
      guideRecommendation: "",
      defaultMeetingPoint: "",
      defaultMeetingTime: "",
      upgradeIds: [],
      faqs: [],
      socialReels: [],
      mapImage: "",
      mapImageId: null,
      relatedItems: [],
      highlights: [],
      schedule: [],
      components: [],
    },
    navigateTo: "/trips",
    slugSourceField: "title",
    stayOnSave: true,
    transformOnLoad: transformTripOnLoad,
    transformOnSave: (data) => {
      // Gallery now uses media-id field (galleryImageIds). Drop the legacy
      // form-only galleryImages object array so we don't send it as the legacy
      // string[] field.
      const { galleryImages: _galleryImages, countryCodesText, ...rest } = data;
      return {
        ...rest,
        // Countries: comma-separated ISO-2 input -> string[] (Block 5.6 country KB).
        countryCodes: (countryCodesText || "")
          .split(",")
          .map((s: string) => s.trim().toUpperCase())
          .filter((s: string) => /^[A-Z]{2}$/.test(s)),
        // Empty-string from "Not set" dropdown option fails the Zod enum — drop it.
        tripStyle: data.tripStyle ? data.tripStyle : undefined,
        included: (data.included || []).map((item) => item.text),
        excluded: (data.excluded || []).map((item) => item.text),
        // Drop blank optional-activity rows ("Add activity" appends an empty one) so the
        // element-level name.min(1) can't 400 the entire trip save and lose unrelated edits.
        optionalActivities: (data.optionalActivities || [])
          .filter((a) => a?.name?.trim())
          .map((a) => ({
            name: a.name.trim(),
            priceNote: a.priceNote?.trim() || undefined,
            description: a.description?.trim() || undefined,
          })),
        // Kusovník: send only the template fields (drop the display-only joined `component`).
        components: (data.components || []).map((cmp, i) => ({
          id: cmp.id,
          componentId: cmp.componentId,
          quantity: cmp.quantity,
          seedTarget: cmp.seedTarget,
          sortOrder: i,
          notes: cmp.notes ?? null,
        })),
      };
    },
    resetDeps: [destinationsData],
  });

  const destinations = destinationsData?.data || [];
  const guides = guidesData?.data || [];
  const allUpgrades = upgradesData?.data || [];
  const allTrips = (allTripsData?.data || []).filter((t) => t.id !== itemData?.id);
  const componentCatalog = componentCatalogData?.data || [];

  // Set upgradeIds from assignments once loaded
  useEffect(() => {
    if (upgradeAssignmentsData?.data) {
      const ids = upgradeAssignmentsData.data.map((a) => a.upgradeId);
      form.setValue("upgradeIds", ids);
    }
  }, [upgradeAssignmentsData, form]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = form;

  const tabsWithErrors = getTabsWithErrors(errors, TRIP_FIELD_TO_TAB);

  // Hide tabs along the two independent axes (Pre-launch Area 3):
  //  - CONTENT (tripKind): experiences show highlights (no itinerary) + the experience tab.
  //  - BOOKING (fulfillmentMode): on-request books on a calendar, so there is no Dates tab.
  // A multi-day on-request TOUR therefore keeps its itinerary but loses the Dates tab.
  const tripKind = watch("tripKind") || "tour";
  const fulfillmentMode = watch("fulfillmentMode") || "fixed_departures";
  const visibleTabs = TABS.filter((t) => {
    if (t.id === "itinerary") return tripKind !== "experience";
    if (t.id === "dates") return fulfillmentMode !== "on_request";
    if (t.id === "experience") return tripKind === "experience";
    return true;
  });

  // After a failed save, useEditPage.onError populates formState.errors via form.setError.
  // Watch saveMutation.error (object identity changes on each failure) and switch to the
  // first errored tab so the user sees the inline errors.
  useEffect(() => {
    if (!saveMutation.error) return;
    const firstTab = getFirstTabWithErrors(form.formState.errors, TRIP_FIELD_TO_TAB);
    if (firstTab && firstTab !== activeTab) setActiveTab(firstTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveMutation.error]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (!isNew && !isLoading && !itemData) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-lg text-gray-600 dark:text-gray-400">Trip not found</p>
        <Link to="/trips" className="btn-primary">
          <ArrowLeft className="h-5 w-5" />
          Back to trips
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/trips" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "New trip" : "Edit trip"}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {isNew ? "Create a new trip" : `Editing: ${itemData?.title}`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <SaveSuccessIndicator isSuccess={saveMutation.isSuccess} />
          {!isNew && watch("slug") && (
            <a
              href={`${WEB_URL}/trip/${watch("slug")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              <ExternalLink className="h-5 w-5" />
              View
            </a>
          )}
          {!isNew && (
            <select {...register("status")} className="input w-40">
              <option value="draft">Draft</option>
              <option value="published">Publish</option>
              <option value="archived">Archive</option>
            </select>
          )}
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className="btn-primary"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-5 w-5" />
                Save
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={saveMutation.error} />

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-4 overflow-x-auto">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const hasErrors = tabsWithErrors.has(tab.id);
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {hasErrors && (
                  <span
                    className="inline-flex h-2 w-2 rounded-full bg-red-500"
                    aria-label="Has validation errors"
                  />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Form */}
      <form id="trip-form" onSubmit={handleSubmit(onSubmit)}>
        <Suspense
          fallback={
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="text-primary-600 h-6 w-6 animate-spin" />
            </div>
          }
        >
          <TripTabContent
            activeTab={activeTab}
            form={form}
            destinations={destinations}
            guides={guides}
            allUpgrades={allUpgrades}
            allTrips={allTrips}
            componentCatalog={componentCatalog}
          />
        </Suspense>
      </form>
    </div>
  );
}
